import {
  Button,
  Checkbox,
  DatePicker,
  Divider,
  Input,
  InputNumber,
  message,
  Select,
  Space,
  Typography,
} from "antd";
import dayjs from "dayjs";
import { useEffect, useRef, useState } from "react";
import { uploadImage } from "../../../../apis/misc.api";
import {
  attachDocumentToPO,
  createReverseFlowRequest,
  getPurchaseOrderDetails,
  getReverseFlowRequestedPOItems,
} from "../../../../apis/po.api";
import {
  fetchPIfromId,
  getEstimateAdditionalChargesReverseRaisedFor,
} from "../../../../apis/pro_forma_invoice.api";
import { getImageUrl } from "../../../../helpers/image_helper";
import { ApprovalReasons } from "../../../../pages/refunds-v2";
import { usePermissions } from "../../../../custom_hooks/permission/permission_hook";

export interface PoRefundDrawerProps {
  po_id: string | number;
  refund_type: "refund" | "return" | "exchange";
  onClose?: () => void;
}

export function PoRefundDrawer({
  po_id,
  refund_type,
  onClose,
}: PoRefundDrawerProps) {
  const { hasPermission } = usePermissions();
  const canEdit = hasPermission("po.update");
  const [poItems, setPoItems] = useState<any[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [itemQuantityMap, setItemQuantityMap] = useState<{
    [key: string]: number;
  }>({});
  const [disabledPoItems, setDisabledPoItems] = useState<any[]>([]);
  const [estimateItemMap, setEstimateItemMap] = useState<{
    [key: string]: {
      estimate_quantity: number;
      total_po_quantity: number;
      total_po_quantity_return: number;
      total_po_quantity_refund: number;
      remaining_quantity: number;
    };
  }>({});
  const [checkedItems, setCheckedItems] = useState<string[]>([]);
  const [applyBifurcation, setApplyBifurcation] = useState(false);
  const [itemBifurcationCharge, setItemBifurcationCharge] = useState<
    "vendor" | "client" | "mdepot" | null
  >(null);
  const [itemBifurcationChargeValue, setItemBifurcationChargeValue] = useState<
    number | null
  >(null);
  const [estimateId, setEstimateId] = useState<number | null>(null);
  const [additionalCharges, setAdditionalCharges] = useState<any[]>([]);
  const [raisedAdditionalCharges, setRaisedAdditionalCharges] = useState<any[]>(
    []
  );
  const [selectedAdditionalCharges, setSelectedAdditionalCharges] = useState<
    any[]
  >([]);
  const [attachmentUrls, setAttachmentUrls] = useState<string[]>([]);

  const [raiseDelivery, setRaiseDelivery] = useState(false);
  const [dispatchTime, setDispatchTime] = useState<string | null>(null);

  const [approvalReason, setApprovalReason] = useState<string | null>(null);
  const [approvalRemark, setApprovalRemark] = useState<string>();
  const [showApprovalRemark, setShowApprovalRemark] = useState(false);

  useEffect(() => {
    if (approvalReason === "Others") {
      setShowApprovalRemark(true);
    } else {
      setShowApprovalRemark(false);
    }
  }, [approvalReason]);

  const getPIAdditionalCharges = async () => {
    if (!estimateId) {
      return [];
    }
    const estimate = await fetchPIfromId(String(estimateId));
    return estimate?.estimate_additional_charges || [];
  };

  const getAlreadyRaisedAdditionalCharges = async () => {
    if (!estimateId) {
      return [];
    }

    const raisedAdditionalCharges =
      getEstimateAdditionalChargesReverseRaisedFor({
        estimate_id: estimateId,
      });
    return raisedAdditionalCharges;
  };

  useEffect(() => {
    if (!po_id) {
      return;
    }

    getPOItems()
      .then((items) => {
        setPoItems(items);
      })
      .catch((error) => {
        console.error("Error fetching PO items:", error);
      });
    getReverseFlowRequestedPOItems({
      po_id: po_id,
    })
      .then((items) => {
        setEstimateItemMap(items);
      })
      .catch((error) => {
        console.error("Error fetching PO items:", error);
      });
  }, [po_id]);

  useEffect(() => {
    console.log("Estimate ID changed:", estimateId);
    getPIAdditionalCharges()
      .then((items) => {
        setAdditionalCharges(items);
      })
      .catch((error) => {
        console.error("Error fetching PO items:", error);
      });
    getAlreadyRaisedAdditionalCharges()
      .then((items) => {
        setRaisedAdditionalCharges(items);
      })
      .catch((error) => {
        console.error("Error fetching PO items:", error);
      });
  }, [estimateId]);

  const getPOItems = async () => {
    if (!po_id) {
      return [];
    }

    const details = await getPurchaseOrderDetails(String(po_id));

    setEstimateId(details?.estimate?.id);
    return details?.po_items || [];
  };

  const submitRequest = async () => {
    if (!canEdit) {
      message.error("You don't have permission to raise this request");
      return;
    }
    if (raiseDelivery && !dispatchTime) {
      message.error("Please select a dispatch time");
      return;
    }
    if (attachmentUrls.length === 0) {
      message.error("Please upload at least one attachment");
      return;
    }
    if (!approvalReason) {
      message.error("Please select a reason for approval");
      return;
    }
    if (showApprovalRemark && !approvalRemark) {
      message.error("Please enter a remark");
      return;
    }

    const itemsReversal = Object.keys(itemQuantityMap)
      .map((key) => {
        return {
          item_id: key,
          quantity: itemQuantityMap[key],
        };
      })
      .filter((item) => item.quantity > 0);

    const payload = {
      po_id: po_id,
      po_type: refund_type,
      po_items_data: itemsReversal,
      reason: showApprovalRemark ? approvalRemark : approvalReason,
    } as Record<string, any>;
    if (selectedAdditionalCharges.length > 0) {
      payload["estimate_additional_charges"] = selectedAdditionalCharges?.map(
        (chargeId) => {
          return {
            id: chargeId,
          };
        }
      );
    }
    if (raiseDelivery) {
      payload["dispatch_time"] = dispatchTime;
    }
    if (applyBifurcation) {
      if (!itemBifurcationCharge) {
        message.error("Please select bifurcation type");
        return;
      }
      if (!itemBifurcationChargeValue) {
        message.error("Please enter bifurcation amount");
        return;
      }

      payload["additional_charge_resposibility"] = itemBifurcationCharge;
      payload["bifurcated_charge"] = itemBifurcationChargeValue;
    }

    const response = await createReverseFlowRequest(payload);

    if (response) {
      // Handle success
      console.log("Request submitted successfully:", response);
      const createdPoIds = (response?.created_po_ids || []) as string[];
      message.loading({
        content: "Finalising request...",
        key: "finalising",
      });
      await Promise.all(
        createdPoIds
          .map((poId: any) => {
            return attachmentUrls.map(async (url: any) => {
              return attachDocumentToPO({
                po_id: poId,
                attachment_url: url,
                attachment_type: "reverse_request_proof",
              });
            });
          })
          .flat()
      );
      message.success({
        content: "Request submitted successfully",
        key: "finalising",
        duration: 2,
      });
    } else {
      // Handle error
      console.error("Error submitting request");
    }

    if (onClose) {
      onClose();
    }
  };

  const additionalChargesAfterFilter = additionalCharges.filter(
    (charge) =>
      !raisedAdditionalCharges.some(
        (raisedCharge) => raisedCharge.id === charge.id
      )
  );

  return (
    <div>
      {/* <pre>
        {JSON.stringify(itemQuantityMap, null, 2)}
        {JSON.stringify(disabledPoItems, null, 2)}
      </pre> */}
      {poItems.filter((item: any) => {
        // Check if there is any valid remaining quantity for the item
        const estimateItemId = String(item?.estimate_item?.id);
        const estimateItemCalc = estimateItemMap[estimateItemId];
        return estimateItemCalc?.remaining_quantity > 0;
      }).length === 0 && (
        <div
          style={{
            textAlign: "center",
            marginTop: "1rem",
            fontSize: "1.2rem",
            color: "#888",
          }}
        >
          No items available for {refund_type}
        </div>
      )}
      {poItems
        .filter((item: any) => {
          // Check if there is any valid remaining quantity for the item
          const estimateItemId = String(item?.estimate_item?.id);
          const estimateItemCalc = estimateItemMap[estimateItemId];
          return estimateItemCalc?.remaining_quantity > 0;
        })
        .map((item: any, index) => {
          return (
            <Space
              key={index}
              style={{
                width: "100%",
                marginBottom: "1rem",
                justifyContent: "space-between",
              }}
            >
              <Checkbox
                checked={
                  checkedItems.includes(item.id) || itemQuantityMap[item.id] > 0
                }
                onChange={(e) => {
                  const checked = e.target.checked;
                  if (checked) {
                    // Add item to the map
                    setItemQuantityMap((prev) => {
                      const newMap = { ...prev };
                      newMap[item.id] = 1;
                      return newMap;
                    });

                    setCheckedItems((prev) => {
                      return [...prev, item.id];
                    });
                  } else {
                    // Remove the item from the map
                    setItemQuantityMap((prev) => {
                      const newMap = { ...prev };
                      newMap[item.id] = 0;
                      return newMap;
                    });

                    setCheckedItems((prev) => {
                      return prev.filter((id) => id !== item.id);
                    });
                  }
                }}
              >
                <Space>
                  <img
                    src={getImageUrl(item?.estimate_item?.item_image, "50")}
                    alt=""
                    width={50}
                    height={50}
                    style={{
                      borderRadius: "5px",
                      objectFit: "contain",
                      backgroundColor: "#f0f0f0",
                    }}
                  />
                  <div>
                    {item?.estimate_item?.boq_item}
                    <div className="text-muted">
                      PI Price: Rs. {item?.estimate_item?.unit_cost_price} PO
                      Price: Rs. {item?.unit_po_price}{" "}
                    </div>
                  </div>
                </Space>
              </Checkbox>
              <InputNumber
                min={0}
                max={
                  estimateItemMap[String(item?.estimate_item?.id)]
                    .remaining_quantity
                }
                value={itemQuantityMap[item.id] ?? 0}
                // disabled={(itemQuantityMap[item.id] ?? 0) === 0}
                onChange={(value) => {
                  setItemQuantityMap((prev) => {
                    const newMap = { ...prev };
                    if (value === undefined || value === null) {
                      newMap[item.id] = 0;
                    } else {
                      newMap[item.id] = value;
                    }
                    return newMap;
                  });
                  if (value === 0) {
                    setCheckedItems((prev) => {
                      return prev.filter((id) => id !== item.id);
                    });
                  } else {
                    setCheckedItems((prev) => {
                      if (!prev.includes(item.id)) {
                        return [...prev, item.id];
                      }
                      return prev;
                    });
                  }
                }}
                style={{ width: "100px" }}
              />
            </Space>
          );
        })}
      <div className="mt-4">
        <Typography.Title level={5}>Additional Charges</Typography.Title>
        {additionalChargesAfterFilter.length === 0 && (
          <div
            style={{
              textAlign: "center",
              marginTop: "1rem",
              fontSize: "1.2rem",
              color: "#888",
            }}
          >
            No additional charges available
          </div>
        )}
        {additionalChargesAfterFilter.map((charge: any, index) => {
          return (
            <div
              key={index}
              style={{
                width: "100%",
                marginBottom: "0.5rem",
              }}
              className="d-flex justify-content-between align-items-center"
            >
              <Checkbox
                checked={selectedAdditionalCharges.includes(charge.id)}
                onChange={(e) => {
                  const checked = e.target.checked;
                  if (checked) {
                    // Add item to the map
                    setSelectedAdditionalCharges((prev) => {
                      return [...prev, charge.id];
                    });
                  } else {
                    // Remove the item from the map
                    setSelectedAdditionalCharges((prev) => {
                      return prev.filter((id) => id !== charge.id);
                    });
                  }
                }}
              >
                {charge?.charge_type}
              </Checkbox>
              <div>
                Rs.{" "}
                {Math.round(
                  parseFloat(charge?.amount) *
                    (1 + parseFloat(charge?.tax_value) / 100)
                )}{" "}
              </div>
            </div>
          );
        })}
      </div>
      <div className="mt-4">
        <Typography.Title level={5}>Attachments</Typography.Title>
        {attachmentUrls?.map((url, index) => {
          return (
            <div
              key={index}
              style={{
                width: "100%",
                marginBottom: "0.5rem",
              }}
              className="d-flex justify-content-between align-items-center"
            >
              <a href={url} target="_blank" rel="noopener noreferrer">
                File {index + 1}
              </a>
            </div>
          );
        })}
        <input
          type="file"
          multiple
          style={{
            width: 1,
            height: 1,
            opacity: 0,
            position: "absolute",
            zIndex: -1,
            pointerEvents: "none",
          }}
          onChange={async (e) => {
            const files = e.target.files;
            if (files) {
              message.loading({
                content: "Uploading files...",
                key: "uploading",
              });
              const urls = await Promise.all(
                Array.from(files).map(async (file) => {
                  const formData = new FormData();
                  formData.append("file", file);
                  return uploadImage(formData);
                })
              );
              setAttachmentUrls((prev) => {
                return [...prev, ...urls];
              });

              message.success({
                content: "Files uploaded successfully",
                key: "uploading",
                duration: 2,
              });
            }
          }}
          ref={fileInputRef}
        />
        <Button
          type="primary"
          onClick={() => {
            if (fileInputRef.current) {
              fileInputRef.current.click();
            }
          }}
          style={{ marginTop: "1rem" }}
        >
          Upload Attachments
        </Button>
      </div>
      <Divider />

      <div className="my-3">
        <Select
          className="w-100"
          placeholder="Select reason"
          onChange={(value) => {
            setApprovalReason(value);
          }}
          value={approvalReason}
        >
          {ApprovalReasons.map((reason) => {
            return (
              <Select.Option key={reason} value={reason}>
                {reason}
              </Select.Option>
            );
          })}
        </Select>
        {showApprovalRemark && (
          <div className="mt-2">
            <Input.TextArea
              placeholder="Enter remark"
              onChange={(e) => {
                setApprovalRemark(e.target.value);
              }}
              value={approvalRemark}
              rows={4}
            />
          </div>
        )}
      </div>
      {/* <div className="mt-3">
        <div className="d-flex align-items-center mb-3 gap-2">
          <Checkbox
            checked={applyBifurcation}
            onChange={(e) => {
              setApplyBifurcation(e.target.checked);
            }}
          />
          <Typography.Title level={5} className="mb-0">
            Loss Bifurcation (if any)
          </Typography.Title>
        </div>
        <div className="d-flex justify-content-between align-items-center">
          <div>
            <Select
              value={itemBifurcationCharge}
              onChange={(value) => {
                setItemBifurcationCharge(value);
              }}
              style={{ width: 150, marginRight: "1rem" }}
              placeholder="Select Type"
              disabled={!applyBifurcation}
              options={[
                { label: "Vendor", value: "vendor" },
                { label: "Client", value: "client" },
                { label: "Material Depot", value: "mdepot" },
              ]}
            />
          </div>
          <div>
            <InputNumber
              min={0}
              value={itemBifurcationChargeValue}
              onChange={(value) => {
                setItemBifurcationChargeValue(value);
              }}
              style={{ width: 150 }}
              placeholder="Enter Amount"
              disabled={!applyBifurcation}
            />
          </div>
        </div>
      </div> */}
      <div className="my-4">
        <Checkbox
          checked={raiseDelivery}
          onChange={(e) => {
            setRaiseDelivery(e.target.checked);
          }}
        >
          Raise Delivery
        </Checkbox>
        {raiseDelivery && (
          <DatePicker
            style={{ marginLeft: "1rem" }}
            onChange={(date, dateString) => {
              setDispatchTime(dateString);
            }}
            placeholder="Select Date"
            format="YYYY-MM-DD"
            disabledDate={(current) => {
              return current && current < dayjs(new Date());
            }}
          />
        )}
      </div>
      <Button
        type="primary"
        onClick={submitRequest}
        style={{ marginTop: "1rem" }}
        disabled={Object.keys(itemQuantityMap).length === 0 || !canEdit}
      >
        Submit Reqeust for {refund_type}
      </Button>
    </div>
  );
}
